/**
 * v2.9.150 — Shazam Badge component
 * ------------------------------------------------------------------
 * Small visual indicator that a station uses Shazam for song
 * recognition. Two modes:
 *
 *   • mandatory  — Shazam is the ONLY recognition path for the
 *                  station (fingerprint_provider="shazam" or
 *                  fingerprint_unlimited=true). Rendered in BLUE
 *                  to signal "critical dependency".
 *
 *   • fallback   — Shazam runs only as a safety net when ICY /
 *                  metadata API fails. Rendered in GRAY to signal
 *                  "secondary".
 *
 * Used in three places (per v2.9.150 product spec):
 *   1. StationCard (admin grid view)
 *   2. CompareStationsPage (next to station name in tables)
 *   3. Station detail header (when implemented)
 *
 * The "S" glyph is rendered as inline SVG so we don't pull in an
 * external image asset and keep bundle size flat.
 *
 * Props:
 *   • mode:  "mandatory" | "fallback" — required; controls color
 *   • size:  "sm" | "md"               — default "sm"
 *   • showLabel:  boolean              — default false; when true
 *                                        shows "Shazam" text label
 *   • className:  string               — additional Tailwind
 *                                        classes to merge
 *
 * Usage:
 *   <ShazamBadge mode="mandatory" />
 *   <ShazamBadge mode="fallback" size="md" showLabel />
 */
import React from 'react';

const SIZE_CLASSES = {
  sm: 'w-4 h-4 text-[10px]',
  md: 'w-5 h-5 text-xs',
};

const MODE_THEME = {
  mandatory: {
    bg: 'bg-blue-500',
    text: 'text-white',
    ring: 'ring-blue-300/40',
    label: 'Shazam-only αναγνώριση',
  },
  fallback: {
    bg: 'bg-gray-400 dark:bg-gray-600',
    text: 'text-white',
    ring: 'ring-gray-300/30',
    label: 'Shazam fallback (ICY primary)',
  },
};

export const ShazamBadge = ({
  mode,
  size = 'sm',
  showLabel = false,
  className = '',
  ...rest
}) => {
  if (!mode || !MODE_THEME[mode]) return null;
  const theme = MODE_THEME[mode];
  const sizeCls = SIZE_CLASSES[size] || SIZE_CLASSES.sm;
  return (
    <span
      data-testid={`shazam-badge-${mode}`}
      title={theme.label}
      className={`inline-flex items-center gap-1 ${className}`}
      {...rest}
    >
      <span
        className={`inline-flex items-center justify-center rounded-full font-bold ${theme.bg} ${theme.text} ring-1 ${theme.ring} ${sizeCls}`}
        aria-label={theme.label}
      >
        S
      </span>
      {showLabel && (
        <span className="text-xs text-gray-600 dark:text-gray-400">
          {mode === 'mandatory' ? 'Shazam-only' : 'Shazam fallback'}
        </span>
      )}
    </span>
  );
};

export default ShazamBadge;
